import {
	InfoCard,
	InputAutocompleteMulti,
	LoadingPage,
} from '@innovyze/stylovyze';
import {
	useGetExternalUsersFromPaginated,
	useGetInternalUsersFromPaginated,
	validateUsersLimit,
} from '@Utils/parseUsersUtil';

import { ManageUsersFormikContext } from '@Types/users.types';
import { Product } from '@Types/product.types';
import React from 'react';
import { getValue } from '@Selectors';
import styled from 'styled-components';
import { useFormikContext } from 'formik';
import useInternalUser from '../../hooks/useInternalUser';
import { useSelector } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';

interface ManageUserFieldsProps {
	product?: Product;
}

const CustomInfoCard = styled(InfoCard)`
	> div {
		min-height: 371px;
	}
`;

const getHelperText = (
	limitSurpassed: boolean,
	limitReached: boolean,
	t: TFunc,
) => {
	if (limitSurpassed) {
		return t(
			// eslint-disable-next-line quotes
			"You don't have enough available seats for this subscription. Contact sales to upgrade",
		);
	}
	if (limitReached) {
		return t('The number of seats for this subscription has been reached.');
	}
	return '';
};

const ManageUserFields = ({ product }: ManageUserFieldsProps) => {
	const { t } = useGlobalization();
	const internalUsers = useGetInternalUsersFromPaginated();
	const externalUsers = useGetExternalUsersFromPaginated();
	const isInternalUser = useInternalUser();

	const { values } = useFormikContext<ManageUsersFormikContext>();
	const { users } = useSelector(getValue).users.paginatedUsers;

	const { limitReached, limitSurpassed } = validateUsersLimit(
		values.externalUsers,
		product,
		externalUsers,
	);

	const usersOptions = users.map(el => ({
		key: el.id,
		value: el.name,
	}));

	const internalUsersOptions = usersOptions.filter(el =>
		internalUsers.map(user => user.id).includes(el.key),
	);
	const externalUsersOptions = usersOptions.filter(el =>
		externalUsers.map(user => user.id).includes(el.key),
	);

	return (
		<CustomInfoCard title={t('Subscribed Users')}>
			{usersOptions ? (
				<>
					<InputAutocompleteMulti
						dataCy="externalUsersInput"
						options={externalUsersOptions}
						fieldAttrs={{ name: 'externalUsers' }}
						label={t('User names')}
						error={limitSurpassed}
						helperText={getHelperText(
							limitSurpassed,
							limitReached,
							t,
						)}
						autocompleteProps={{
							limitTags: 100,
							disableClearable: true,
							getOptionDisabled: () => limitReached,
						}}
					/>
					{isInternalUser && (
						<InputAutocompleteMulti
							dataCy="internalUsersInput"
							options={internalUsersOptions}
							fieldAttrs={{ name: 'internalUsers' }}
							label={t('Internal Autodesk users')}
							error={limitSurpassed}
							helperText={t(
								'Internal Autodesk users are not included in the seat count',
							)}
							autocompleteProps={{
								limitTags: 100,
								disableClearable: true,
							}}
						/>
					)}
				</>
			) : (
				<LoadingPage />
			)}
		</CustomInfoCard>
	);
};

export default ManageUserFields;
